import React, { Component } from 'react';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import {
  isProviderUser,
  isUserAuthorized,
  needsEmailVerificationReminder,
} from '../../util/userHelpers';
import { matchPathname } from '../../util/routes';

import { Modal } from '../../components';

import EmailReminder from './EmailReminder';
import PhotoReminder from './PhotoReminder';
import css from './ModalMissingInformation.module.css';

// D51: the email-verification reminder never opens on sign-in / sign-up / email / password pages, its
// own targets (/account with its contact details, /profile-settings), in the middle of a flow (listing
// wizard, checkout-type pages) or on transaction / chat pages. Route names from routeConfiguration.
const EMAIL_REMINDER_EXCLUDED_PAGES = [
  'LoginPage',
  'SignupPage',
  'SignupForUserTypePage',
  'ConfirmPage',
  'PasswordRecoveryPage',
  'PasswordResetPage',
  'EmailVerificationPage',
  'ProfileSettingsPage',
  'AccountSettingsPage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'StripePayoutOnboardingPage',
  'PaymentMethodsPage',
  'ManageAccountPage',
  'NewListingPage',
  'EditListingPage',
  'EditListingStripeOnboardingPage',
  'CheckoutPage',
  'MakeOfferPage',
  'RequestQuotePage',
  'OrderDetailsPage',
  'OrderDetailsPageRedirect',
  'SaleDetailsPage',
  'SaleDetailsPageRedirect',
];

// The photo reminder (D46) stays away from its own target and from pages in the middle of a flow;
// inbox and chat pages are not excluded
const PHOTO_REMINDER_EXCLUDED_PAGES = [
  'LoginPage',
  'SignupPage',
  'AccountSettingsPage',
  'ContactDetailsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'StripePayoutPage',
  'ProfileSettingsPage',
  'NewListingPage',
  'EditListingPage',
  'EditListingStripeOnboardingPage',
  'CheckoutPage',
  'MakeOfferPage',
  'RequestQuotePage',
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const PHOTO_SIGNUP = 'Signup';
const PHOTO_PUBLISH = 'Publish';
const PHOTO_SIGNUP_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

// Email reminder memory in this browser, per user (D51): { closedAt: ISO date }. Closed by any
// method, it stays away for 72 hours, except right after a published listing. Storage can be
// missing (SSR) or blocked (private mode): then nothing is remembered and it may show again.
const EMAIL_REMINDER_PAUSE_MS = 72 * 60 * 60 * 1000;
const emailReminderKey = userId => `delowoEmailReminder:v1:${userId}`;
const isEmailReminderPaused = userId => {
  try {
    const { closedAt } = JSON.parse(window.localStorage.getItem(emailReminderKey(userId))) || {};
    const closedTime = closedAt ? new Date(closedAt).getTime() : NaN;
    return Date.now() - closedTime < EMAIL_REMINDER_PAUSE_MS;
  } catch (e) {
    return false;
  }
};
const writeEmailReminderClosed = userId => {
  try {
    window.localStorage.setItem(
      emailReminderKey(userId),
      JSON.stringify({ closedAt: new Date().toISOString() })
    );
  } catch (e) {
    // not remembered
  }
};

const routeNamesOf = (location, routeConfiguration) =>
  matchPathname(location.pathname, routeConfiguration).map(m => m.route.name);

// Photo reminder memory in this browser, per user (D46): { shownSignup: ISO date, pendingPublish: true }.
// Storage can be missing (SSR) or blocked (private mode): then nothing is remembered.
const photoReminderKey = userId => `delowoPhotoReminder:v1:${userId}`;
const readPhotoReminder = userId => {
  try {
    return JSON.parse(window.localStorage.getItem(photoReminderKey(userId))) || {};
  } catch (e) {
    return {};
  }
};
const writePhotoReminder = (userId, changes) => {
  try {
    const { pendingPublish, ...rest } = { ...readPhotoReminder(userId), ...changes };
    const next = pendingPublish ? { ...rest, pendingPublish } : rest;
    window.localStorage.setItem(photoReminderKey(userId), JSON.stringify(next));
  } catch (e) {
    // not remembered
  }
};

class ModalMissingInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: false,
    };
    this.isEmailReminderDue = this.isEmailReminderDue.bind(this);
    this.handleMissingInformationReminder = this.handleMissingInformationReminder.bind(this);
    this.handlePhotoReminder = this.handlePhotoReminder.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  componentDidMount() {
    // A page reached in the app mounts its own Topbar with the user already loaded
    const user = ensureCurrentUser(this.props.currentUser);
    this.handleMissingInformationReminder(user);
    this.handlePhotoReminder(user);
  }

  componentDidUpdate() {
    const user = ensureCurrentUser(this.props.currentUser);
    this.handleMissingInformationReminder(user);
    this.handlePhotoReminder(user);
  }

  // D51: the email reminder is due for its audience on a page where it may open, unless it was
  // closed in this browser less than 72 hours ago; right after a published listing it is due anyway
  isEmailReminderDue(currentUser) {
    const {
      currentUserHasListings,
      currentUserHasOrders,
      emailReminderPublishPending,
      location,
      routeConfiguration,
    } = this.props;
    const hasListings = currentUserHasListings || emailReminderPublishPending;
    if (!needsEmailVerificationReminder(currentUser, hasListings, currentUserHasOrders)) {
      return false;
    }
    const routeNames = routeNamesOf(location, routeConfiguration);
    if (routeNames.some(name => EMAIL_REMINDER_EXCLUDED_PAGES.includes(name))) {
      return false;
    }
    return !!emailReminderPublishPending || !isEmailReminderPaused(currentUser.id.uuid);
  }

  // One reminder window per page view (a page mounts its own Topbar): nothing opens after a
  // reminder was shown on this page
  handleMissingInformationReminder(currentUser) {
    const { showMissingInformationReminder, hasSeenMissingInformationReminder } = this.state;
    if (showMissingInformationReminder || hasSeenMissingInformationReminder) {
      return;
    }
    if (this.isEmailReminderDue(currentUser)) {
      this.setState({ showMissingInformationReminder: EMAIL_VERIFICATION });
    }
  }

  // Profile-photo reminder (D46) for a user without a photo who is not hidden from the home feed:
  // after every published service (pending until closed), else once per browser after a
  // specialist's signup (account up to 14 days old, no published service yet). One dialog per page
  // view: the email reminder first when it is due (D51), else this one; after the email reminder
  // this one waits for the next page view.
  handlePhotoReminder(currentUser) {
    const {
      location,
      routeConfiguration,
      photoReminderPublishPending,
      publishedServiceCheck,
      onFetchHasPublishedService,
      onClearPhotoReminderPublishPending,
    } = this.props;
    const userId = currentUser.id?.uuid;
    if (!userId || !isUserAuthorized(currentUser)) {
      return;
    }

    // The publish moment survives a reload, the redirect, a remounted Topbar and pages where the
    // reminder cannot show; it is cleared when the reminder is closed
    const stored = readPhotoReminder(userId);
    if (photoReminderPublishPending && !stored.pendingPublish) {
      writePhotoReminder(userId, { pendingPublish: true });
    }
    const publishPending = photoReminderPublishPending || stored.pendingPublish;

    // profileImage null = loaded and empty; a user fetched without the relationship (checkout,
    // payment methods) has none of it (ensureCurrentUser gives {}): nothing is decided then
    const { profileImage } = currentUser;
    const hasPhoto = !!profileImage?.id;
    const hiddenFromHomeFeed = currentUser.attributes.profile?.metadata?.hideFromHomeFeed === true;
    if (hasPhoto || hiddenFromHomeFeed) {
      if (publishPending) {
        writePhotoReminder(userId, { pendingPublish: null });
        onClearPhotoReminderPublishPending?.();
      }
      return;
    }
    if (profileImage !== null) {
      return;
    }

    const { showMissingInformationReminder, hasSeenMissingInformationReminder } = this.state;
    const routeNames = routeNamesOf(location, routeConfiguration);
    if (
      showMissingInformationReminder ||
      hasSeenMissingInformationReminder ||
      routeNames.some(name => PHOTO_REMINDER_EXCLUDED_PAGES.includes(name)) ||
      this.isEmailReminderDue(currentUser)
    ) {
      return;
    }

    if (publishPending) {
      this.setState({ showMissingInformationReminder: PHOTO_PUBLISH });
      return;
    }

    const createdAt = currentUser.attributes.createdAt;
    const isNewAccount =
      !!createdAt && Date.now() - new Date(createdAt).getTime() < PHOTO_SIGNUP_WINDOW_MS;
    if (!isProviderUser(currentUser) || !isNewAccount || stored.shownSignup) {
      return;
    }
    if (publishedServiceCheck?.userId !== userId) {
      onFetchHasPublishedService?.(userId);
    } else if (publishedServiceCheck.hasPublishedService === false) {
      this.setState({ showMissingInformationReminder: PHOTO_SIGNUP });
    }
  }

  // Every way of closing (the actions, the corner close, Esc, a click outside; for the email one also
  // leaving through its «Исправить.» link to /account) settles the reminder:
  // the email one pauses for 72 hours in this browser and its publish moment is used up (D51); the
  // signup photo one is marked as shown in this browser, the publish photo one stops pending
  handleClose() {
    const {
      currentUser,
      onClearPhotoReminderPublishPending,
      onClearEmailReminderPublishPending,
    } = this.props;
    const reminder = this.state.showMissingInformationReminder;
    const userId = currentUser?.id?.uuid;
    if (userId && reminder === EMAIL_VERIFICATION) {
      writeEmailReminderClosed(userId);
      onClearEmailReminderPublishPending?.();
    } else if (userId && reminder === PHOTO_SIGNUP) {
      writePhotoReminder(userId, { shownSignup: new Date().toISOString() });
    } else if (userId && reminder === PHOTO_PUBLISH) {
      writePhotoReminder(userId, { pendingPublish: null });
      onClearPhotoReminderPublishPending?.();
    }
    this.setState({
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: true,
    });
  }

  render() {
    const {
      rootClassName,
      className,
      containerClassName,
      currentUser,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onManageDisableScrolling,
      onResendVerificationEmail,
    } = this.props;

    const user = ensureCurrentUser(currentUser);
    const classes = classNames(rootClassName || css.root, className);

    let content = null;

    const reminder = this.state.showMissingInformationReminder;
    const isPhotoReminder = reminder === PHOTO_SIGNUP || reminder === PHOTO_PUBLISH;
    const currentUserLoaded = user && user.id;
    if (currentUserLoaded && isUserAuthorized(currentUser)) {
      if (isPhotoReminder) {
        content = (
          <PhotoReminder className={classes} moment={reminder} onClose={this.handleClose} />
        );
      } else if (reminder === EMAIL_VERIFICATION) {
        content = (
          <EmailReminder
            className={classes}
            user={user}
            onResendVerificationEmail={onResendVerificationEmail}
            onFixEmail={this.handleClose}
            sendVerificationEmailInProgress={sendVerificationEmailInProgress}
            sendVerificationEmailError={sendVerificationEmailError}
          />
        );
      }
    }

    // The photo reminder has its own «Позже» / «Не сейчас» button; its corner close keeps «Закрыть»
    const closeButtonMessage = isPhotoReminder ? null : (
      <FormattedMessage id="ModalMissingInformation.closeVerifyEmailReminder" />
    );

    return (
      <Modal
        id="MissingInformationReminder"
        containerClassName={containerClassName}
        isOpen={!!reminder}
        onClose={this.handleClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        closeButtonMessage={closeButtonMessage}
        closeOnOutsideClick={isPhotoReminder}
      >
        {content}
      </Modal>
    );
  }
}

/**
 * Modal that tells user that they have not saved all the information the service needs.
 * This is used to remind user that they need to verify their email address, and a specialist
 * without a profile photo that the home feed shows only profiles with one (D46).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string} props.id
 * @param {Object} props.currentUser API entity
 * @param {Function} props.onManageDisableScrolling
 * @param {Object?} props.sendVerificationEmailError
 * @param {boolean} props.sendVerificationEmailInProgress
 * @param {boolean?} props.photoReminderPublishPending a service was published in this session
 * @param {boolean?} props.emailReminderPublishPending a listing was published in this session (D51)
 * @param {Function?} props.onClearEmailReminderPublishPending
 * @param {Object?} props.publishedServiceCheck { userId, inProgress, hasPublishedService }
 * @param {Function?} props.onFetchHasPublishedService
 * @param {Function?} props.onClearPhotoReminderPublishPending
 * @returns {JSX.Element} Modal element if user needs to be reminded
 */
const EnhancedModalMissingInformation = props => {
  const routeConfiguration = useRouteConfiguration();

  return <ModalMissingInformation routeConfiguration={routeConfiguration} {...props} />;
};

export default EnhancedModalMissingInformation;
